/* eslint-disable no-undef */
import React, { PureComponent } from 'react';
import 'aframe';
import moment from 'moment';
import Hls from 'hls.js';
import './controls/OrbitControls';
import './components/aframeHelpers';
import './AFramePanorama.scss';
import { getMaxTextureSize } from '../../utils/webglSupport';
import { get360VideoSource, isVideoPreview } from 'utils/videoHelpers';

import { customGaEvent, GA_EVENTS, GA_VIDEO_TYPES } from '../../utils/ga';
import MainMediaControls from '../ViewWrapperContainer/ViewWrapper/MainMediaControls/MainMediaControls';
import TourLogoWrapper from '../ViewWrapperContainer/ViewWrapper/TourLogoWrapper/TourLogoWrapper';
import CloseButton from './components/VRControls/NavigationButtons/ImageButton';
import closeBtn from '../../images/close-btn@3x.png';
import {
  exitVRMode,
  enterVRMode,
  toggleFullScreen,
  modifyFooterClass,
  modifyCanvasClass,
  FADE_OUT_TIME,
  FADE_IN_TIME,
  setCamera,
} from './components/AFrameShared';
import HotspotWrapperContainer from '../Hotspot/HotspotWrapperContainer';
import StopMediaHTML from './components/VRControls/StopMedia/StopMediaHTML';
import PanoControls from '../Popup/PanoControls/PanoControls';
import VRControls from './components/VRControls/VRControls';
import StreamingOverlay from './streamingOverlay/StreamingOverlay';
import { Gesture } from 'react-with-gesture';
import { ClipLoader } from 'react-spinners';
import { fetchFileSize } from 'api/helpers/helpers';
import * as constants from "../../constants";

const defaultView = {
  position: { x: 0, y: 0, z: -0.2 },
  rotation: { x: -3.508354649267438e-15, y: 0, z: 0 },
};

const initialSpecState = {
  specs: {},
  currentFragmentIndex: null,
};

class AFrameVideo extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      id: '',
      showPano: true,
      playing: false,
      videoCanPlay: false,
      currentTime: 0,
      duration: 0,
      autoRotate: false,
      autoRotateDirection: 'counterclockwise',
      pauseAutoRotate: false,
      VRMode: false,
      VRStopMedia: false,
      showHotspots: false,
      videoSource: '',
      videoSourceInitialized: false,
      isIOS: false,
      hotspotContentOpen: false,
      numberOfLoops: 0,
      lastVideoProgress: 0,
      gaVideoTypeText: GA_VIDEO_TYPES.PROGRESSIVE_DOWNLOAD,
    };
    this.initTimestamp = null;
    this.camera = null;
    this.video = null;
    this.scene = null;
    this.hls = null;
    this.streamingSpecs = { ...initialSpecState };
    this.currentBandwidth = null;
    this.currentStreamingResolution = null;
    this.fileSize = null;
  }

  componentDidMount() {
    const { id } = this.props;
    // eslint-disable-next-line no-undef
    const isIOS = AFRAME.utils.device.isIOS();
    this.setState({ id, isIOS }, () => {
      this.initialize();
      this.setVideoSource();
    });

    window.addEventListener('keydown', this.shortcutListener);
  }

  initialize() {
    const { initialView, history } = this.props;
    const isPreview = isVideoPreview(history);
    this.cameraRig = document.getElementById('camera-rig');
    this.camera = document.getElementById('camera');
    this.video = document.getElementById('video');
    this.audio = document.getElementById('audio');
    this.sky = document.getElementById('sky');
    this.scene = document.getElementById('scene');
    this.maxTextureSize = getMaxTextureSize();
    this.addListeners();
    this.initializeHandleExitVR();
    this.setPanoView(initialView);
    if (!isPreview) {
      this.sendGAEventsFromDOMListeners();
    }
  }

  setVideoSource() {
    const { id: videoId, videoSourceInitialized, isIOS } = this.state;
    const { history } = this.props;
    const isPreview = isVideoPreview(history);
    const videoSource = get360VideoSource({
      videoId,
      isPreview,
      isIOS,
      maxTextureSize: this.maxTextureSize,
    });
    const isAdpStream = videoId.includes('.m3u8');
    this.initTimestamp = moment();
    this.currentBandwidth = null;
    if (isAdpStream && !isPreview) {
      this.setState(
        {
          gaVideoTypeText: GA_VIDEO_TYPES.ADAPTIVE_STREAMING,
          videoSource,
          videoSourceInitialized: true,
        },
        () => {
          !videoSourceInitialized && this.initializeHls();
        }
      );
    } else {
      fetchFileSize(
        videoSource,
        fileSize => (this.fileSize = `${(fileSize / 1e6).toFixed(2)}Mb`)
      );
      // uses mp4 version for preview or when adaptive streaming is not used
      this.video.preload = 'auto';
      this.setState({
        gaVideoTypeText: GA_VIDEO_TYPES.PROGRESSIVE_DOWNLOAD,
        videoSource,
      });
    }
  }

  initializeHls() {
    const { videoSource, isIOS } = this.state;
    const videoEl = document.getElementById('video');
    const mimeType = 'video/MP2T';
    videoEl.preload = 'none';
    // if HLS is supported natively, browser will handle it (iOS, MacOS).
    if (
      ('MediaSource' in window && MediaSource.isTypeSupported(mimeType)) ||
      isIOS ||
      videoEl.canPlayType('application/vnd.apple.mpegurl')
    ) {
      videoEl.src = videoSource;
      videoEl.load();
      // if not available, check if the browser will support MediaSource Extensions (most do).
      // for a complete list, look here: https://caniuse.com/mediasource
    } else if (Hls.isSupported()) {
      // startLevel: -1 works in conunction with testBandwidth
      // testBandwidth is set to true by default
      this.hls = new Hls({
        startLevel: -1,
      });
      this.hls.loadSource(videoSource);
      this.hls.attachMedia(this.video);
      this.listenToHlsEvents();
    }
  }

  listenToHlsEvents() {
    this.hls.on(Hls.Events.FRAG_BUFFERED, (eventName, data) => {
      const {
        stats: { total, loading, buffering, parsing },
        frag: { sn }, // the fragment's number
      } = data;
      const { currentLevelIndex, _levels } = this.hls.levelController;
      const calcBitrate = Math.round(
        (8 * total) / (buffering.end - loading.start)
      );
      const buffer = (buffering.end - parsing.end).toFixed(1);
      const resolution = _levels[currentLevelIndex].attrs['RESOLUTION'];
      const bandwidth = this.hls.bandwidthEstimate.toFixed(3);
      const bandwidthToMegabits = (bandwidth / 1e6).toFixed(1);
      this.currentBandwidth = bandwidthToMegabits;
      this.currentStreamingResolution = resolution;
      const bitrate = `${(calcBitrate / 1000).toFixed(1)} Mb/s`;
      this.streamingSpecs.specs = {
        ...this.streamingSpecs.specs,
        [sn]: {
          buffer,
          resolution,
          bandwidth: `${bandwidthToMegabits} Mb/s`,
          bitrate,
          currentLevelIndex,
        },
      };
    });
    this.hls.on(Hls.Events.FRAG_CHANGED, (eventName, data) => {
      this.streamingSpecs.currentFragmentIndex = data.frag.sn;
    });
    this.hls.on(Hls.Events.LEVEL_SWITCHED, (eventName, data) => {
      this.handleCustomGAEvent(GA_EVENTS.VIDEO_360_ADAPTIVE_QUALITY_CHANGE);
    });
    this.hls.on(Hls.Events.MEDIA_DETACHED, (eventName, data) => {
      this.streamingSpecs = {
        ...initialSpecState,
      };
    });
  }

  destroyHls() {
    this.hls.destroy();
  }

  handleHlsError(hls) {
    hls.on(Hls.Events.ERROR, (event, data) => {
      if (data.fatal) {
        switch (data.type) {
          case Hls.ErrorTypes.NETWORK_ERROR:
            // try to recover network error
            console.log('fatal network error encountered, trying to recover');
            hls.startLoad();
            break;
          case Hls.ErrorTypes.MEDIA_ERROR:
            console.log('fatal media error encountered, trying to recover');
            hls.recoverMediaError();
            break;
          default:
            // cannot recover
            console.log('fatal error, cannot recover');
            hls.destroy();
            break;
        }
      }
    });
  }

  shortcutListener = evt => {
    const { showSpecOverlay } = this.state;
    const shortcutActive = evt.keyCode === 88 && evt.ctrlKey && evt.altKey;
    if (shortcutActive) {
      this.setState({ showSpecOverlay: !showSpecOverlay });
    }
  };

  // setting the timeouts in a class method helps us when using clearTimeout
  sourceFadeTimer = () => {
    const { id, initialView } = this.props;
    setTimeout(() => {
      this.setState({ id }, () => {
        this.setVideoSource();
        this.handleNewStop();
        this.setPanoView(initialView);
      });
    }, FADE_OUT_TIME);
  };

  componentDidUpdate(prevProps, prevState) {
    const { playing, showPano, currentTime, duration } = this.state;
    const {
      id,
      initialView,
      stopDescriptionMinimized,
      currentStop,
      activeStop,
    } = this.props;
    if (!prevState.showPano && showPano) {
      this.initialize();
    }
    if (!prevState.playing && playing) {
      this.video.play();
    }
    if (prevState.playing && !playing) {
      this.video.pause();
    }
    // handle source fades
    if (id !== undefined && prevProps.id !== id && this.video) {
      this.hls && this.destroyHls();
      this.setState({
        showHotspots: false,
        videoSourceInitialized: false,
        videoSource: '',
        hotspotContentOpen: false,
      });
      clearTimeout(this.sourceFadeTimer);
      this.sky.emit('fadeout');
      this.sourceFadeTimer();
    }
    if (prevState.VRMode && !this.state.VRMode) {
      this.camera.setAttribute('active', true);
      // look controls wanted to take over after vr mode
      this.camera.setAttribute('look-controls', 'enabled', false);
      // eslint-disable-next-line no-undef
      if (AFRAME.utils.device.isIOS()) {
        // canvas gets out of wack on iOS
        setTimeout(() => {
          this.scene.canvas.style.width = '100%';
          this.scene.canvas.style.height = '100%';
        }, 400);
      }
    }
    if (!prevState.VRMode && this.state.VRMode) {
      this.camera.setAttribute('active', false);
      if (initialView.rotation) {
        this.setVRView(initialView.rotation);
      } else {
        this.setVRView(defaultView.rotation);
      }
    }
    if (
      prevProps.stopDescriptionMinimized &&
      !stopDescriptionMinimized &&
      playing
    ) {
      // if this is a mobile user who has opened the stop description while the video is playing, pause the video
      this.handlePlay();
    }

    if (prevProps.activeStop !== activeStop) {
      this.setPauseAutoRotate(false);
    }

    // reset video state once it's done playing and isn't on auto loop
    if (
      playing &&
      currentStop &&
      !currentStop.autoLoop &&
      currentTime === duration
    ) {
      this.handleVideoEnd();
    }

    if (prevState.playing && this.state.playing) {
      this.handleSendingGaEventEveryTenPercent();
    }
  }

  componentWillUnmount() {
    this.removeListeners();
    clearTimeout(this.sourceFadeTimer);
    window.removeEventListener('keydown', this.shortcutListener);
  }

  handleSendingGaEventEveryTenPercent = () => {
    const { currentTime, duration } = this.state;
    const percentageOfVideoPlayed = (currentTime / duration) * 100;
    const timeStamps = [10, 20, 30, 40, 50, 60, 70, 80, 90];
    const percentageOfVideoPlayedRounded =
      Math.round(percentageOfVideoPlayed / 10) * 10;
    const sendEventObj = () =>
      this.handleCustomGAEvent(GA_EVENTS.VIDEO_360_PROGRESS, {
        percent_360_video: percentageOfVideoPlayedRounded,
      });
    const isCurrentPercentageWithinTimeStamp = timeStamps.some(
      percent => Math.abs(percent - percentageOfVideoPlayed) < 1
    );
    if (
      isCurrentPercentageWithinTimeStamp &&
      this.state.lastVideoProgress !== percentageOfVideoPlayedRounded
    ) {
      sendEventObj();
      this.setState({
        lastVideoProgress: percentageOfVideoPlayedRounded,
      });
    } else if (this.state.lastVideoProgress && percentageOfVideoPlayed > 95) {
      this.setState({ lastVideoProgress: 0 });
    }
  };

  setPanoView = initialView => {
    const {
      autoRotate: stateAutoRotate,
      autoRotateDirection: stateAutoRotateDirection,
    } = this.state;
    const {
      autoRotate: propsAutoRotate,
      autoRotateDirection: propsAutoRotateDirection,
    } = this.props;
    if (propsAutoRotate !== undefined && stateAutoRotate !== propsAutoRotate) {
      // previewing from stop will produce autoRotate of undefined
      // comparing state & props will reduce needless setState
      this.setState({ autoRotate: propsAutoRotate });
    }
    if (stateAutoRotateDirection !== propsAutoRotateDirection) {
      this.setState({ autoRotateDirection: propsAutoRotateDirection });
    }
    if (initialView && initialView.position && initialView.rotation) {
      setCamera(this.camera, initialView);
    } else {
      setCamera(this.camera, defaultView);
    }
  };

  setVRView = rotation => {
    rotation && this.cameraRig.setAttribute('rotation', { y: rotation.y });
    // eslint-disable-next-line
    AFRAME.utils.device.isMobile() &&
      this.cameraRig.setAttribute('position', { x: 0, y: -1.6, z: 0 });
  };

  setExitVRView = () => {
    this.cameraRig.setAttribute('rotation', { y: 0 });
    this.cameraRig.setAttribute('position', { x: 0, y: 0, z: 0 });
  };

  cb = e => this.props.getPanoRotation(e, this.camera);

  setDuration = () => {
    this.setState(() => ({ duration: this.video.duration }));
  };

  handlePlay = () => {
    this.setState(({ playing }) => ({ playing: !playing, numberOfLoops: 0 }));
  };

  handleTimeUpdate = () => {
    this.setState(() => ({ currentTime: this.video.currentTime }));
  };

  onVideoCanPlay = e => {
    this.setState({ videoCanPlay: true });
  };

  onLoadedData = e => {
    this.setState({ videoCanPlay: false });
  };

  handleNewStop = () => {
    this.setState({ playing: false });
  };

  isVideoMp4 = () => {
    const videoSrc = this.video.getAttribute('src');
    return videoSrc.includes('.mp4');
  };

  setIOSShader = () => {
    if (!AFRAME.utils.device.isIOS()) return;
    const isMp4 = this.isVideoMp4();
    const shader = isMp4 ? 'standard' : 'flat';
    this.sky.setAttribute('material', 'shader', shader);
    const mesh = this.sky.getObject3D('mesh');
    if (mesh && mesh.material.map) {
      mesh.material.map.flipY = true;
    }
  }

  addListeners = () => {
    this.camera.addEventListener('componentchanged', this.cb);
    this.video.addEventListener('loadedmetadata', this.setDuration);

    if (this.sky) {
      this.sky.addEventListener('loaded', () => {
        this.setIOSShader();
        this.sky.emit('fadein');
      });

      this.sky.addEventListener('materialvideoloadeddata', e => {
        this.setIOSShader();
        this.sky.emit('fadein');
      });

      this.sky.addEventListener('animationcomplete__fadein', () => {
        this.setState({ showHotspots: true });
        this.sky.setAttribute('fullOpacity', true); // used for testing purposes
      });
    }

    if (this.scene) {
      this.scene.addEventListener('contentopen', e => {
        // once a user has interacted with content hide the video play button
        this.setState({ hotspotContentOpen: true });
      });
      this.scene.addEventListener('hotspot-focus', this.handleUserInteraction);
      this.scene.addEventListener('user-interaction-detected', this.handleUserInteraction);
    }
  };

  sendGAEventsFromDOMListeners = () => {
    this.video.addEventListener('loadeddata', e => {
      const loadedTimestamp = moment();
      const diff = loadedTimestamp.diff(this.initTimestamp);
      this.handleCustomGAEvent(GA_EVENTS.VIDEO_360_FIRST_FRAME_LOAD, {
        first_frame_360_video: diff,
      });
    });
    this.video.addEventListener('pause', () => {
      const { currentTime, duration } = this.state;
      const percentageOfVideoPlayed = Math.floor(
        (currentTime / duration) * 100
      );
      const percentageRoundedDown =
        Math.floor(percentageOfVideoPlayed / 10) * 10;
      percentageRoundedDown !== 100 &&
        this.handleCustomGAEvent(GA_EVENTS.VIDEO_360_PAUSE, {
          percent_360_video: percentageRoundedDown,
        });
    });
  };

  removeListeners = () => {
    if (this.camera) {
      this.camera.removeEventListener('componentchanged', this.cb);
    }
    if (this.video) {
      this.video.removeEventListener('loadedmetadata', this.setDuration);
      this.video.removeEventListener('pause', this.handleCustomGAEvent);
      this.video.removeEventListener('ended', this.handleCustomGAEvent);
    }

    if (this.scene) {
      this.scene.removeEventListener('exit-vr', () => {
        console.log('exit-vr listener removed');
      });
      this.scene.removeEventListener('user-interaction-detected', this.handleUserInteraction);
      this.scene.removeEventListener('hotspot-focus', this.handleUserInteraction);
    }
    if (this.sky) {
      this.sky.removeEventListener('loaded', () => {
        console.log('loaed listener removed');
      });

      this.sky.removeEventListener('animationcomplete__fadein', () => {});

      this.sky.removeEventListener('materialvideoloadeddata', e => {
        console.log('materialvideoloadeddata listener removed');
      });
    }

    if (this.scene) {
      this.scene.removeEventListener('contentopen', () => {});
    }
  };

  updateVRStates = enterOrExit => {
    if (enterOrExit) {
      // entered VR
      this.setState({
        VRMode: true,
      });
    } else if (!enterOrExit) {
      // exited VR
      this.setState({
        VRMode: false,
        VRStopMedia: false,
      });
    }
  };

  toggleVR = enter => {
    const { tourData, history, aid } = this.props;
    const { VRMode } = this.state;

    if (!VRMode && enter && this.scene.hasLoaded) {
      enterVRMode(tourData, history, this.scene, aid);
      this.updateVRStates(true);
    } else {
      exitVRMode(this.scene, tourData, aid);
      this.updateVRStates(false);
    }
  };

  toggleStopMedia = () => {
    this.setState(state => ({ VRStopMedia: !state.VRStopMedia }));
  };

  initializeHandleExitVR = () => {
    this.scene.addEventListener('exit-vr', () => {
      this.updateVRStates(false);
      modifyCanvasClass(false);
      modifyFooterClass(false);
      this.setExitVRView();
    });
  };

  toggleAutoRotate = () => {
    const { tourData } = this.props;
    const { autoRotate } = this.state;

    this.setState(
      prevState => ({ autoRotate: !prevState.autoRotate }),
      () => {
        if (tourData) {
          customGaEvent({
            action: GA_EVENTS.AUTO_ROTATE,
            label: autoRotate ? 'Start' : 'Stop',
          });
        }
      }
    );
  };

  setPauseAutoRotate = isPaused => {
    this.setState({ pauseAutoRotate: isPaused });
  };

  // turn off rotate when user interacts with the scene
  handleUserInteraction = () => {
    this.setState({
      autoRotate: false,
    });
  };

  handleExitFullscreen = () => {
    // should handle chrome and ff
    if (typeof document.exitFullscreen === 'function') {
      document.exitFullscreen();
    }

    // new webkit
    if (typeof document.webkitExitFullscreen === 'function') {
      document.webkitExitFullscreen();
    }

    // old webkit
    if (typeof document.webkitCancelFullScreen === 'function') {
      document.webkitCancelFullScreen();
    }
  };

  handleVideoEnd = () => {
    this.setState({ playing: false, currentTime: 0 });
  };

  handleVideoLoop = () => {
    const { numberOfLoops } = this.state;
    this.video.currentTime = 0;
    this.video.play();
    this.setState({ numberOfLoops: numberOfLoops + 1 });
  };

  sendGAEventsOnPlay = () => {
    const { numberOfLoops } = this.state;
    numberOfLoops < 2 &&
      numberOfLoops > 0 &&
      this.handleCustomGAEvent(GA_EVENTS.VIDEO_360_LOOP);
    !numberOfLoops &&
      this.video.currentTime === 0 &&
      this.handleCustomGAEvent(GA_EVENTS.VIDEO_360_START);
  };

  handleCustomGAEvent = (eventName, parameters = {}) => {
    const { percent_360_video } = parameters;
    const { gaVideoTypeText, lastVideoProgress, id } = this.state;
    const { currentStop } = this.props;
    const isAdpStream = id.includes('.m3u8');
    const resolution = isAdpStream
      ? this.currentStreamingResolution
      : `${this.video.videoWidth}x${this.video.videoHeight}`;
    const videoLength = this.video.duration.toFixed(2);
    customGaEvent({
      action: `${eventName} ${gaVideoTypeText}`,
      label: `${currentStop?.panoId || 'Label Not Provided'}`,
      resolution_360_video: resolution,
      length_360_video: videoLength,
      current_bandwidth: this.currentBandwidth,
      size_360_video: this.fileSize,
      percent_360_video: percent_360_video || lastVideoProgress,
      ...parameters,
    });
  };

  handleOnEnded = () => {
    const { numberOfLoops } = this.state;
    const { currentStop } = this.props;
    const shouldContinueLoop = currentStop?.autoLoop && numberOfLoops < 2;
    shouldContinueLoop ? this.handleVideoLoop() : this.handleVideoEnd();
  };

  render() {
    const {
      autoRotate,
      autoRotateDirection,
      pauseAutoRotate,
      currentTime,
      duration,
      hotspotContentOpen,
      playing,
      videoCanPlay,
      showHotspots,
      VRMode,
      VRStopMedia,
      videoSource,
      id: videoId,
      showSpecOverlay,
    } = this.state;
    const {
      activeStop,
      changeStop,
      checkLogoURL,
      closeShareModal,
      currentStop,
      history,
      initialView,
      isMainMediaPlaying,
      isMobile,
      miniMapView,
      panoHasKeyboardControl,
      primaryColor,
      publicTour,
      resetInitialView,
      setYPosition,
      showPanoControls,
      showVRMode,
      stops,
      tier,
      toggleMainMediaStatus,
      togglePanoControls,
      tourData,
      tourPublished,
      viewOnly,
      aid,
      accountPermissions = {},
    } = this.props;
    const { specs, currentFragmentIndex } = this.streamingSpecs;
    const hotspots = currentStop && currentStop.hotspots;
    const hasHotspots = hotspots && Object.keys(hotspots).length > 0;
    const clickHandler = ({ initial, target, xy }) => {
      const isVRMode =
        typeof target.className === 'string' &&
        target.className.includes('vr-mode');
      if (!hasHotspots && !isVRMode && target.localName === 'canvas') {
        // check for no drag
        if (initial[0] === xy[0] && initial[1] === xy[1]) {
          this.handlePlay();
        }
      }
    };
    const isFullscreen =
      document.fullscreen || document.webkitCurrentFullScreenElement;
    const shouldShowHotspots =
      hasHotspots && showHotspots && accountPermissions.hotspots;
    const ariaMessage = videoCanPlay
      ? 'Loading complete'
      : 'Loading 360 video...';
    const isAdpStream = videoId.includes('.m3u8');
    const isMini = miniMapView === constants.VIDEOS360;

    return (
      <Gesture onUp={clickHandler}>
        {() => (
          <div
            id="video360"
            onMouseDown={resetInitialView}
          >
            <div className="sr-only" aria-live="assertive" role="alert">
              <p>{ariaMessage}</p>
            </div>
            {!videoCanPlay && (
              <div className="video-loader">
                <ClipLoader size={150} color="#ffffff" />
              </div>
            )}
            {tourData && !isMini &&  (
              <TourLogoWrapper
                currentTour={tourData}
                tier={tier}
                checkLogoURL={checkLogoURL}
                publicTour={publicTour}
              />
            )}
            <a-scene
              id="scene"
              cursor="rayOrigin: mouse; fuseTimeout: 0;"
              raycaster="objects: .clickable;"
              vr-mode-ui={`enabled: true`}
              device-orientation-permission-ui="enabled: false"
              loading-screen="enabled: false"
              webgl-context
              embedded
              role="region"
              aria-label={`${isMini ? 'Mini interactive 360-degree video' : 'Interactive 360-degree video'}`}
            >
              {miniMapView === 'map' && (
                <React.Fragment>
                  {!isMobile && showPanoControls && (
                    <PanoControls
                      mediaType="360° video"
                      tourData={tourData}
                      togglePanoControls={togglePanoControls}
                      showPanoControls={showPanoControls}
                    />
                  )}
                  {isAdpStream && showSpecOverlay && (
                    <StreamingOverlay
                      specs={specs}
                      currentFragmentIndex={currentFragmentIndex}
                    />
                  )}
                  <MainMediaControls
                    miniMapView={miniMapView}
                    VRMode={VRMode}
                    closeShareModal={closeShareModal}
                    currentTime={currentTime}
                    duration={duration}
                    handlePlay={this.handlePlay}
                    isVideo360
                    videoCanPlay={videoCanPlay}
                    panoRotating={autoRotate}
                    playing={playing}
                    primaryColor={primaryColor}
                    progress={(currentTime / duration) * 100}
                    setYPosition={setYPosition}
                    showVRMode={showVRMode}
                    stopIds={tourData && tourData.stopIds}
                    stops={tourData && stops}
                    tier={tier}
                    toggleAutoRotate={this.toggleAutoRotate}
                    autoRotateDirection={autoRotateDirection}
                    toggleFullScreen={() => toggleFullScreen(tourData, aid)}
                    toggleVR={this.toggleVR}
                    tourPublished={tourPublished}
                    viewOnly={viewOnly}
                    minimizePlayButton={hotspotContentOpen}
                  />
                </React.Fragment>
              )}
              <a-videosphere
                id="sky"
                opacity="0"
                rotation="0 180 0"
                src="#video"
                animation__fadeout={`
                    property: material.opacity;
                    startEvents: fadeout;
                    to: 0;
                    dur: ${FADE_OUT_TIME}
                  `}
                animation__fadein={`
                    property: material.opacity;
                    startEvents: fadein;
                    to: 1;
                    dur: ${FADE_IN_TIME}
                  `}
              />
              <a-entity id="cameratarget"></a-entity>
              {shouldShowHotspots && (
                <HotspotWrapperContainer
                  history={history}
                  handlePlay={this.handlePlay}
                  changeStop={changeStop}
                  setPauseAutoRotate={this.setPauseAutoRotate}
                  playing={playing}
                  hotspots={hotspots}
                  hotspotIcons={tourData?.styles?.hotspotIcons}
                  tourId={tourData?.id}
                  tourName={tourData?.name}
                  vrMode={VRMode}
                  setActiveHotspot={activeHotspot =>
                    this.setState({ activeHotspot })
                  }
                />
              )}
              <a-entity id="camera-rig">
                <a-camera
                  id="camera"
                  position="0 0 -0.2"
                  wasd-controls-enabled="false"
                  orbit-controls={`
                      autoRotate: ${autoRotate && !pauseAutoRotate};
                      autoRotateSpeed: ${
                        autoRotateDirection !== 'clockwise' ? -0.05 : 0.05
                      };
                      target: #cameratarget;
                      enableDamping: true;
                      enableKeys:${panoHasKeyboardControl};
                      enableZoom: true;
                      dampingFactor: 0.11;
                      rotateSpeed: -0.14;
                      touchRotateSpeed: -0.05;
                      minZoom: 0.75;
                      maxZoom: 2;
                      initialZoom: 0.75;
                      minDistance: 0.15;
                      maxDistance: 0.15;
                      autoVRLookCam: true;
                      stopPropagation: ${!isMobile};`}
                >
                  {isFullscreen && !VRMode && (
                    <CloseButton
                      id="fullscreen-exit"
                      src={'#exit-fullscreen-btn'}
                      height={0.4}
                      width={0.4}
                      handleClick={this.handleExitFullscreen}
                      position={'7.6 4.5 -6'}
                    />
                  )}
                </a-camera>
                {VRMode && (
                  <a-camera
                    id="vr-camera"
                    position="0 0 0"
                    user-height="0"
                    wasd-controls-enabled="false"
                    look-controls-enabled="true"
                    disable-culling // fixes iOS stacked box VR issue [TB-2024]
                  >
                    <a-entity
                      geometry="primitive: box;"
                      material="shader: html; target: #htmlElement;"
                    />
                    <a-entity
                      position="0 0 -3"
                      geometry="primitive: ring; thetaLength: 360; radiusOuter: 0.06; radiusInner: 0.04;"
                      material="color: #009c6b; shader: flat; transparent: true;"
                    />
                    <a-entity
                      position="0 0 -3"
                      geometry="primitive: ring; thetaLength: 360; radiusOuter: 0.06; radiusInner: 0.04;"
                      material="color: #fff; shader: flat; transparent: true;"
                      raycaster="interval: 100; objects: .clickable; recursive: true;"
                      cursor="fuse: true; fuseTimeout: 500;"
                      animation="property: geometry.thetaLength; to: 0; startEvents: fusing; dur: 500;"
                      animation__leave="property: geometry.thetaLength; to: 360; startEvents: mouseleave; dur: 500;"
                    />
                  </a-camera>
                )}
              </a-entity>
              {VRMode && (
                <VRControls
                  toggleVR={this.toggleVR}
                  changeStop={changeStop}
                  activeStop={activeStop}
                  currentStop={currentStop}
                  toggleStopMedia={this.toggleStopMedia}
                  initialView={initialView}
                  handleVideoPlay={this.handlePlay}
                  videoIsPlaying={playing}
                  video
                  toggleMainMediaStatus={toggleMainMediaStatus}
                  isMainMediaPlaying={isMainMediaPlaying}
                />
              )}
              <a-assets id="assets">
                <video
                  id="video"
                  preload="auto"
                  style={{ display: 'none' }}
                  autoPlay={false}
                  crossOrigin="anonymous"
                  playsInline
                  onTimeUpdate={this.handleTimeUpdate}
                  onEnded={this.handleOnEnded}
                  onPlay={this.sendGAEventsOnPlay}
                  src={videoSource}
                  onCanPlay={this.onVideoCanPlay}
                  onLoadStart={this.onLoadedData}
                />
              </a-assets>
              <img
                src={closeBtn}
                id="exit-fullscreen-btn"
                alt=""
                crossOrigin="anonymous"
              />
            </a-scene>
            {VRMode && VRStopMedia && (
              <StopMediaHTML
                name={currentStop.name}
                description={currentStop.description}
                toggleStopMedia={this.toggleStopMedia}
              />
            )}
          </div>
        )}
      </Gesture>
    );
  }
}

export default AFrameVideo;
